import dayjs from 'dayjs'
import { ReactElement } from 'react'
import { Box, Button, Card, TextField, Typography } from '@mui/material'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { NavigateNext, Cancel } from '@mui/icons-material'
import type { Course, LearningPath, TrainingSeries } from 'src/v3/core/types'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { paperInputSx } from '../../styles'
import CourseSelectionCard from '../../components/course-selection-card'

type FormValues = {
  name: string
  linkedServiceID: string
  dueDate: string
  courseIDs: string[]
}

type FormErrors = {
  name?: string
  linkedServiceID?: string
  dueDate?: string
  courseIDs?: string
}

type AssignmentDetailsCourseStepProps = {
  values: FormValues
  errors: FormErrors
  courses: Course[]
  trainingSeries: TrainingSeries[]
  learningPaths?: LearningPath[]
  form: any
  onCancel: () => void
  onContinue: () => void
  canContinue: boolean
  isLoadingCourses?: boolean
  hasAnyProgress?: boolean
  linkedServiceID?: string
  isEditMode?: boolean
}

export default function AssignmentDetailsCoursesStep({
  courses,
  trainingSeries,
  learningPaths,
  form,
  onCancel,
  onContinue,
  canContinue,
  isLoadingCourses = false,
  hasAnyProgress = false,
  linkedServiceID,
  isEditMode = false,
}: AssignmentDetailsCourseStepProps): ReactElement {
  useScrollToTop()

  return (
    <>
      <Card className="p-6 mb-6">
        <Typography variant="h6" component="h2" className="mb-3">
          Assignment Details
        </Typography>
        <Box className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <form.Field
            name="name"
            validators={{
              onChange: ({ value }: { value: string }) =>
                !value ? 'Assignment name is required' : undefined,
            }}
          >
            {(field: any) => (
              <TextField
                label="Assignment Name"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={!field.state.meta.isValid}
                helperText={field.state.meta.errors.join(', ')}
                fullWidth
                required
                sx={paperInputSx}
              />
            )}
          </form.Field>

          <form.Field
            name="dueDate"
            validators={{
              onChange: ({ value }: { value: string }) => {
                if (!value) return 'Due date is required'
                if (!dayjs(value, 'YYYY-MM-DD', true).isValid())
                  return 'Invalid due date'
                return undefined
              },
            }}
          >
            {(field: any) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Due Date"
                  value={field.state.value ? dayjs(field.state.value) : null}
                  onChange={(newValue) => {
                    field.handleChange(
                      newValue ? dayjs(newValue).format('YYYY-MM-DD') : '',
                    )
                  }}
                  minDate={isEditMode ? undefined : dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !field.state.meta.isValid,
                      helperText: field.state.meta.errors.join(', '),
                      onBlur: field.handleBlur,

                      slotProps: {
                        input: {
                          sx: { bgcolor: 'background.paper' },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          </form.Field>
        </Box>
      </Card>
      <form.Field
        name="courseIDs"
        mode="array"
        validators={{
          onChange: ({ value }: { value: string[] }) => {
            if (!value || value.length === 0) {
              return 'At least one course must be selected'
            }
            return undefined
          },
        }}
      >
        {(field: any) => (
          <CourseSelectionCard
            courses={courses}
            trainingSeries={trainingSeries}
            learningPaths={learningPaths}
            selectedCourseIDs={field.state.value}
            onSelectionChange={(ids) => field.handleChange(ids)}
            isLoadingCourses={isLoadingCourses}
            hasAnyProgress={hasAnyProgress}
            linkedServiceID={linkedServiceID}
            error={
              !field.state.meta.isValid
                ? field.state.meta.errors.join(', ')
                : undefined
            }
          />
        )}
      </form.Field>
      <Box className="flex justify-end gap-3">
        <Button
          variant="outlined"
          onClick={onCancel}
          size="large"
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={!canContinue}
          size="large"
          endIcon={<NavigateNext />}
        >
          Continue to User Selection
        </Button>
      </Box>
    </>
  )
}
